/**
 * Shared GLSL snippets for the grain sim.
 *
 * Collision uses an analytic SDF of up to 16 boxes + the ground plane.
 * `sampleColliderSDF` is the swap point: a later 3D-texture / baked-SDF
 * variant can replace the body of that function without touching the
 * integrate / response code that calls it.
 */

export const SIM_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

export const COLLIDER_GLSL = /* glsl */ `
uniform vec3 uBoxCenter[16];
uniform vec3 uBoxHalf[16];
uniform int uBoxCount;
uniform sampler2D tHeight;
uniform sampler2D tBase;
uniform vec2 uArenaMin;
uniform vec2 uArenaSize;

float sdBox(vec3 p, vec3 c, vec3 h) {
  vec3 q = abs(p - c) - h;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

// Analytic arena SDF (boxes + y=0 plane). Swap the body for a 3D-texture
// sample when a baked scene SDF lands — the collision response below
// only ever calls this function + its finite-difference gradient.
float sampleColliderSDF(vec3 p) {
  float d = p.y;
  for (int i = 0; i < 16; i++) {
    if (i >= uBoxCount) break;
    d = min(d, sdBox(p, uBoxCenter[i], uBoxHalf[i]));
  }
  return d;
}

vec3 colliderGradient(vec3 p) {
  float e = 0.006;
  return normalize(vec3(
    sampleColliderSDF(p + vec3(e, 0.0, 0.0)) - sampleColliderSDF(p - vec3(e, 0.0, 0.0)),
    sampleColliderSDF(p + vec3(0.0, e, 0.0)) - sampleColliderSDF(p - vec3(0.0, e, 0.0)),
    sampleColliderSDF(p + vec3(0.0, 0.0, e)) - sampleColliderSDF(p - vec3(0.0, 0.0, e))
  ));
}

// Local surface = static base elevation (box top or floor) + granular (R) + film (A).
float sampleHeightfield(vec2 xz) {
  vec2 uv = (xz - uArenaMin) / uArenaSize;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 0.0;
  vec4 hf = texture2D(tHeight, uv);
  return hf.r + hf.a + texture2D(tBase, uv).r;
}

vec3 heightfieldNormal(vec2 xz) {
  float e = 0.04;
  float hL = sampleHeightfield(xz - vec2(e, 0.0));
  float hR = sampleHeightfield(xz + vec2(e, 0.0));
  float hD = sampleHeightfield(xz - vec2(0.0, e));
  float hU = sampleHeightfield(xz + vec2(0.0, e));
  return normalize(vec3(hL - hR, 2.0 * e, hD - hU));
}
`;

export const INTEGRATE_GLSL = /* glsl */ `
const float GRAVITY = 9.81;
const float SETTLE_SPEED = 0.14;
const float BOUNCE_KILL = 0.09;
const float CONTACT_EPS = 0.012;

void collide(
  inout vec3 pos,
  inout vec3 vel,
  float radius,
  float restitution,
  float friction
) {
  float boxD = sampleColliderSDF(pos);
  float hf = sampleHeightfield(pos.xz);
  float hfD = pos.y - hf;
  // Only collide with the heightfield when the grain is near it. A floor
  // grain whose XZ sits under a box top must not be launched up the cliff.
  bool hfValid = hfD > -radius * 2.0;
  vec3 n;
  float d;
  if (hfValid && hfD < boxD) {
    d = hfD;
    n = heightfieldNormal(pos.xz);
  } else {
    d = boxD;
    n = colliderGradient(pos);
  }
  if (d < radius) {
    pos += n * (radius - d);
    float vn = dot(vel, n);
    if (vn < 0.0) {
      vec3 vN = n * vn;
      vec3 vT = vel - vN;
      vel = vT * max(0.0, 1.0 - friction) - vN * restitution;
      if (dot(vel, vel) < BOUNCE_KILL * BOUNCE_KILL) vel = vec3(0.0);
    }
  }
}

void integrateGrain(
  inout vec3 pos,
  inout vec3 vel,
  float radius,
  float restitution,
  float friction,
  float drag,
  float dt
) {
  vel.y -= GRAVITY * dt;
  float speed = length(vel);
  const float GRAIN_VMAX = 16.0;
  if (speed > GRAIN_VMAX) {
    vel *= GRAIN_VMAX / max(speed, 1.0e-6);
    speed = GRAIN_VMAX;
  }
  float damp = 1.0 / (1.0 + drag * speed * dt);
  vel *= damp;
  pos += vel * dt;
  collide(pos, vel, radius, restitution, friction);
  // Soft arena walls so grains don't vanish off the map.
  vec2 mn = uArenaMin + vec2(0.05);
  vec2 mx = uArenaMin + uArenaSize - vec2(0.05);
  if (pos.x < mn.x) { pos.x = mn.x; vel.x = abs(vel.x) * 0.2; }
  if (pos.x > mx.x) { pos.x = mx.x; vel.x = -abs(vel.x) * 0.2; }
  if (pos.z < mn.y) { pos.z = mn.y; vel.z = abs(vel.z) * 0.2; }
  if (pos.z > mx.y) { pos.z = mx.y; vel.z = -abs(vel.z) * 0.2; }
}

bool shouldSplat(vec3 pos, vec3 vel, float radius) {
  float speed = length(vel);
  if (speed > SETTLE_SPEED) return false;
  float hf = sampleHeightfield(pos.xz);
  float hfD = pos.y - hf;
  // Contact with the LOCAL surface (floor pile or box-top pile). The lower
  // bound rejects grains sitting at the foot of a wall whose XZ still
  // samples the wall-top base — those must not stamp the elevated texel.
  if (hfD < radius + CONTACT_EPS && hfD > -(radius + CONTACT_EPS)) return true;
  // Settled on a box top: splat at this XZ even if tBase hasn't been
  // sampled yet (deposit still renders on the elevated patch).
  float boxD = sampleColliderSDF(pos);
  vec3 n = colliderGradient(pos);
  return boxD < radius + CONTACT_EPS && n.y > 0.7;
}

// Grit: die on first ground / box-top / pile contact (upward normal).
// Vertical wall hits must NOT kill the spray — those grains fall and
// splat when they meet the floor, or time out at maxLife.
bool shouldSplatGrit(vec3 pos, float radius) {
  float hf = sampleHeightfield(pos.xz);
  float hfD = pos.y - hf;
  if (hfD < radius + CONTACT_EPS && hfD > -(radius + CONTACT_EPS)) return true;
  float boxD = sampleColliderSDF(pos);
  vec3 n = colliderGradient(pos);
  return boxD < radius + CONTACT_EPS && n.y > 0.45;
}
`;

export function posFragSource(): string {
  return /* glsl */ `
uniform sampler2D tPos;
uniform sampler2D tVel;
uniform sampler2D tMeta;
uniform sampler2D tOrigin;
uniform float uDt;
varying vec2 vUv;
${COLLIDER_GLSL}
${INTEGRATE_GLSL}
void main() {
  vec4 posAge = texture2D(tPos, vUv);
  vec4 velLife = texture2D(tVel, vUv);
  vec4 meta = texture2D(tMeta, vUv);
  // -2 = splatted last step → recycle. Other negatives stay dead.
  if (posAge.w < -1.5 && posAge.w > -2.5) {
    gl_FragColor = vec4(0.0, -100.0, 0.0, -1.0);
    return;
  }
  if (posAge.w < 0.0) {
    gl_FragColor = posAge;
    return;
  }
  vec3 pos = posAge.xyz;
  vec3 vel = velLife.xyz;
  float radius = max(meta.x, 0.004);
  integrateGrain(pos, vel, radius, meta.y, meta.z, velLife.w, uDt);
  float age = posAge.w + uDt;
  float maxLife = texture2D(tOrigin, vUv).w;
  if (maxLife < 0.05) maxLife = 8.0;
  bool grit = maxLife < 1.5;
  if (grit) {
    // Contact or timeout both deposit — grit becomes the pile/film.
    if (shouldSplatGrit(pos, radius) || age > maxLife) {
      gl_FragColor = vec4(pos, -2.0);
      return;
    }
    gl_FragColor = vec4(pos, age);
    return;
  }
  if (shouldSplat(pos, vel, radius)) {
    gl_FragColor = vec4(pos, -2.0);
    return;
  }
  if (age > maxLife) {
    gl_FragColor = vec4(pos, -1.0);
    return;
  }
  gl_FragColor = vec4(pos, age);
}
`;
}

export function velFragSource(): string {
  return /* glsl */ `
uniform sampler2D tPos;
uniform sampler2D tVel;
uniform sampler2D tMeta;
uniform sampler2D tOrigin;
uniform float uDt;
varying vec2 vUv;
${COLLIDER_GLSL}
${INTEGRATE_GLSL}
void main() {
  vec4 posAge = texture2D(tPos, vUv);
  vec4 velLife = texture2D(tVel, vUv);
  vec4 meta = texture2D(tMeta, vUv);
  if (posAge.w < -1.5 && posAge.w > -2.5) {
    gl_FragColor = vec4(0.0);
    return;
  }
  if (posAge.w < 0.0) {
    gl_FragColor = velLife;
    return;
  }
  vec3 pos = posAge.xyz;
  vec3 vel = velLife.xyz;
  float radius = max(meta.x, 0.004);
  integrateGrain(pos, vel, radius, meta.y, meta.z, velLife.w, uDt);
  float age = posAge.w + uDt;
  float maxLife = texture2D(tOrigin, vUv).w;
  if (maxLife < 0.05) maxLife = 8.0;
  bool grit = maxLife < 1.5;
  bool dead = grit
    ? (shouldSplatGrit(pos, radius) || age > maxLife)
    : (shouldSplat(pos, vel, radius) || age > maxLife);
  if (dead) {
    gl_FragColor = vec4(0.0);
    return;
  }
  gl_FragColor = vec4(vel, velLife.w);
}
`;
}

export const RENDER_VERT = /* glsl */ `
attribute float aId;
uniform sampler2D tPos;
uniform sampler2D tVel;
uniform sampler2D tMeta;
uniform sampler2D tColor;
uniform sampler2D tOrigin;
uniform float uTexSize;
uniform float uSpriteScale;
uniform float uSpriteHard;
varying vec3 vColor;
varying vec2 vLocal;
varying float vAlpha;
varying float vHard;

void main() {
  float id = aId;
  vec2 tuv = (vec2(mod(id, uTexSize), floor(id / uTexSize)) + 0.5) / uTexSize;
  vec4 posAge = texture2D(tPos, tuv);
  vec4 col = texture2D(tColor, tuv);
  vec4 meta = texture2D(tMeta, tuv);
  vLocal = uv - 0.5;
  vHard = uSpriteHard;
  if (posAge.w < 0.0) {
    vAlpha = 0.0;
    vColor = vec3(0.0);
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  float maxLife = texture2D(tOrigin, tuv).w;
  bool grit = maxLife > 0.05 && maxLife < 1.5;
  vec4 viewCenter = viewMatrix * vec4(posAge.xyz, 1.0);
  vec2 q;
  if (grit) {
    // Near-subpixel at gameplay distance; motion stretch turns the spray
    // into streaks instead of a bouncing-dot field.
    float radius = max(meta.x * 2.2, 0.0016) * max(uSpriteScale, 0.15);
    vec3 viewVel = mat3(viewMatrix) * texture2D(tVel, tuv).xyz;
    vec2 dir = viewVel.xy;
    float spd = length(dir);
    vec2 along = spd > 1.0e-4 ? dir / spd : vec2(1.0, 0.0);
    vec2 perp = vec2(-along.y, along.x);
    float stretch = 1.0 + min(spd * 0.55, 18.0);
    q = along * position.x * radius * 2.0 * stretch + perp * position.y * radius * 2.0;
    vAlpha = 0.55;
    vHard = max(vHard, 0.35);
  } else {
    float spin = col.a;
    float ang = posAge.w * spin;
    float c = cos(ang);
    float s = sin(ang);
    // Visual size is larger than the collision radius so the spray reads on camera.
    // Keep these as grit, not cloud-sized discs — 0.07m floor read as painted bubbles.
    float radius = max(meta.x * 2.6, 0.018) * max(uSpriteScale, 0.15);
    q = vec2(c * position.x - s * position.y, s * position.x + c * position.y) * radius * 2.0;
    vAlpha = 0.72;
  }
  viewCenter.xy += q;
  vColor = col.rgb;
  gl_Position = projectionMatrix * viewCenter;
}
`;

export const RENDER_FRAG = /* glsl */ `
varying vec3 vColor;
varying vec2 vLocal;
varying float vAlpha;
varying float vHard;
vec3 plAcesGrain(vec3 x) {
  const float a = 2.51;
  const float b = 0.03;
  const float c = 2.43;
  const float d = 0.59;
  const float e = 0.14;
  return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
}
void main() {
  if (vAlpha < 0.01) discard;
  float r = length(vLocal);
  // Soft disc, no time term — settled frames are identical.
  // vHard tightens the falloff so mobile grains read as grains, not clouds.
  float inner = mix(0.10, 0.30, clamp(vHard, 0.0, 1.0));
  float a = smoothstep(0.5, inner, r) * vAlpha;
  if (a < 0.02) discard;
  vec3 graded = plAcesGrain(vColor * 1.08);
  vec3 srgb = linearToOutputTexel(vec4(graded, 1.0)).rgb;
  gl_FragColor = vec4(srgb, a);
}
`;

export const SPLAT_VERT = /* glsl */ `
attribute float aId;
uniform sampler2D tPos;
uniform sampler2D tMeta;
uniform sampler2D tOrigin;
uniform sampler2D tVel;
uniform float uTexSize;
uniform vec2 uArenaMin;
uniform vec2 uArenaSize;
uniform float uPointSize;
uniform float uSplatMode;
// 1 = live fines: distance falloff from epicenter + haze deposits nothing.
// 0 = grains / chunk volume stamps (pixel-identical prior path).
uniform float uFineAttenuate;
// 1 = grain stamps may apply brick grit scale + crater-vicinity falloff
// when tOrigin.w (maxLife) marks the particle as grit (< 1.5).
uniform float uGrainAttenuate;
uniform float uGritPointSize;
varying float vMat;
varying float vAmount;
varying float vAlive;
varying float vGritKernel;

float matFromKind(float k) {
  if (k < 1.5) return 0.0;
  if (k < 2.5) return 0.5;
  return 1.0;
}

void main() {
  vec2 tuv = (vec2(mod(aId, uTexSize), floor(aId / uTexSize)) + 0.5) / uTexSize;
  vec4 posAge = texture2D(tPos, tuv);
  vec4 meta = texture2D(tMeta, tuv);
  // pos.w == -2 marks a grain/fine that just settled and must splat once.
  if (posAge.w > -1.5 || posAge.w < -2.5) {
    vAlive = 0.0;
    vMat = 0.0;
    vAmount = 0.0;
    vGritKernel = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 1.0;
    return;
  }
  vec2 uv = (posAge.xz - uArenaMin) / uArenaSize;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    vAlive = 0.0;
    vMat = 0.0;
    vAmount = 0.0;
    vGritKernel = 0.0;
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    gl_PointSize = 1.0;
    return;
  }
  vAlive = 1.0;
  float radius = max(meta.x, 0.004);
  float volume = 4.18879020479 * radius * radius * radius;
  // mode 0: grain volume + materialId in meta.w
  // mode 1: fine depositMass in meta.w, material from kind (meta.z)
  float fineAmt = meta.w;
  if (uSplatMode > 0.5 && uFineAttenuate > 0.5) {
    // Haze subset (long linger) is micron-scale — no visible height.
    float linger = texture2D(tVel, tuv).w;
    if (linger >= 4.5) {
      fineAmt = 0.0;
    } else {
      // Full visual height within ~1.2 m of epicenter, ~20% by ~3 m.
      vec3 origin = texture2D(tOrigin, tuv).xyz;
      float dist = length(posAge.xz - origin.xz);
      fineAmt *= mix(1.0, 0.2, smoothstep(1.2, 3.0, dist));
    }
  }
  vAmount = mix(volume, fineAmt, uSplatMode);
  // Brick grit: restore pre-scale sphere volume (collision radius is
  // gritSizeScale 0.14×), then 0.07× visual + crater-vicinity falloff.
  // Literals must match splat.ts GRAIN_DEPOSIT_SCALE_BRICK / GRIT_SIZE_SCALE_BRICK.
  vGritKernel = 0.0;
  if (uSplatMode < 0.5 && uGrainAttenuate > 0.5) {
    float maxLife = texture2D(tOrigin, tuv).w;
    if (maxLife > 0.05 && maxLife < 1.5) {
      float r = max(meta.x, 1.0e-5);
      float vol = 4.18879020479 * r * r * r;
      vec3 origin = texture2D(tOrigin, tuv).xyz;
      float dist = length(posAge.xz - origin.xz);
      float fall = mix(1.0, 0.45, smoothstep(1.6, 4.0, dist));
      float restore = 1.0 / (0.14 * 0.14 * 0.14);
      vAmount = vol * restore * 0.07 * fall;
      vGritKernel = 1.0;
    }
  }
  vMat = mix(meta.w, matFromKind(meta.z), uSplatMode);
  gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0);
  gl_PointSize = max(vGritKernel > 0.5 ? uGritPointSize : uPointSize, 1.0);
}
`;

export const SPLAT_FRAG = /* glsl */ `
uniform float uCellArea;
uniform float uVisualGain;
// 1 / Σ kernel weights over the pointSize×pointSize pixel grid so total
// deposited height equals (volume / cellArea) * visualGain (mass-conserving).
uniform float uKernelInvSum;
uniform float uKernelSharpness;
uniform float uGritInvSum;
uniform float uGritSharpness;
uniform float uFreshness;
// 1 = write height to A (film / fines). 0 = write height to R (granular).
uniform float uWriteFilm;
varying float vMat;
varying float vAmount;
varying float vAlive;
varying float vGritKernel;
void main() {
  if (vAlive < 0.5) discard;
  // gl_PointCoord ∈ [0,1]² over the point sprite; centre at 0.5.
  vec2 p = gl_PointCoord - 0.5;
  float sharp = mix(uKernelSharpness, uGritSharpness, vGritKernel);
  float invSum = mix(uKernelInvSum, uGritInvSum, vGritKernel);
  float w = exp(-dot(p, p) * sharp);
  if (w < 0.02) discard;
  float h = (vAmount / max(uCellArea, 1.0e-12)) * uVisualGain * w * invSum;
  float film = step(0.5, uWriteFilm);
  gl_FragColor = vec4(h * (1.0 - film), h * vMat, uFreshness * w * invSum, h * film);
}
`;

export const REDUCE_FRAG = /* glsl */ `
uniform sampler2D tSrc;
uniform vec2 uSrcSize;
uniform int uMode;
varying vec2 vUv;
void main() {
  vec2 base = (gl_FragCoord.xy - 0.5) * 2.0;
  float acc = 0.0;
  for (int y = 0; y < 2; y++) {
    for (int x = 0; x < 2; x++) {
      vec2 src = base + vec2(float(x) + 0.5, float(y) + 0.5);
      if (src.x >= uSrcSize.x || src.y >= uSrcSize.y) continue;
      vec4 s = texture2D(tSrc, src / uSrcSize);
      if (uMode == 0) {
        if (s.w >= 0.0) acc += 1.0;
      } else if (uMode == 2) {
        // Heightfield visual mass: granular R + film A.
        acc += s.r + s.a;
      } else {
        acc += s.r;
      }
    }
  }
  gl_FragColor = vec4(acc, 0.0, 0.0, 1.0);
}
`;

export const COPY_FRAG = /* glsl */ `
uniform sampler2D tSrc;
varying vec2 vUv;
void main() {
  gl_FragColor = texture2D(tSrc, vUv);
}
`;

export const SAMPLE_FRAG = /* glsl */ `
uniform sampler2D tHeight;
uniform vec2 uUv;
varying vec2 vUv;
void main() {
  gl_FragColor = texture2D(tHeight, uUv);
}
`;

export const COMPOSE_FRAG = /* glsl */ `
uniform sampler2D tHeight;
uniform sampler2D tDelta;
varying vec2 vUv;
void main() {
  gl_FragColor = texture2D(tHeight, vUv) + texture2D(tDelta, vUv);
}
`;

export const CLEAR_FRAG = /* glsl */ `
void main() {
  gl_FragColor = vec4(0.0);
}
`;

export const HF_PREVIEW_FRAG = /* glsl */ `
uniform sampler2D tHeight;
uniform float uMaxH;
varying vec2 vUv;
void main() {
  vec4 s = texture2D(tHeight, vUv);
  float dep = s.r + s.a;
  float h = dep / max(uMaxH, 1.0e-4);
  float mixId = dep > 1.0e-5 ? s.g / dep : 0.0;
  vec3 brick = vec3(0.50, 0.22, 0.15);
  vec3 conc = vec3(0.50, 0.50, 0.48);
  vec3 stone = vec3(0.27, 0.28, 0.30);
  vec3 tint = mixId < 0.5
    ? mix(brick, conc, mixId * 2.0)
    : mix(conc, stone, (mixId - 0.5) * 2.0);
  vec3 heat = mix(vec3(0.05, 0.07, 0.09), tint, clamp(h * 1.4, 0.0, 1.0));
  gl_FragColor = vec4(heat, 1.0);
}
`;
